import api from '../../services/api';

/*
 * Putting away: taking the merchandise from where it landed and placing it
 * where it sells.
 *
 * What there is to put away comes from two places:
 *
 *   receiving  what was counted in a receipt and is still in the receiving
 *              zone (or in no location, if the warehouse had no dock). The
 *              everyday case.
 *   restock    what was left on the packing table of an order cancelled after
 *              it was picked. Real stock that no location holds and no order
 *              can reserve until somebody puts it away again.
 *
 * Both end in the same movement of the kardex, and only then does the stock
 * count as available to sell.
 */

/**
 * What there is to put away in ONE warehouse, each row with the destination
 * the server suggests and why. `counts` always carries both sources, so the
 * tabs can say how much there is on the other side without a second call.
 *
 * @param {object} params - `warehouse` (mandatory), `source ('receiving'|'restock')`, `company`.
 */
export const getPutawayPendingRequest = async (params) => {
  return await api.get('inventories/putaway/pending/', { params });
};

/**
 * What was already put away in one warehouse, newest first, as it was left in
 * the kardex.
 *
 * @param {object} params - `warehouse` (mandatory), `company`, `page`, `page_size`.
 */
export const getPutawayHistoryRequest = async (params) => {
  return await api.get('inventories/putaway/history/', { params });
};

/**
 * Puts away from receiving.
 *
 * @param {object} payload - `product_id, from_slot_id, warehouse_id,
 *   to_slot_id, quantity, lot, reference, notes`
 */
export const putawayRequest = async (payload) => {
  return await api.post('inventories/putaway/', payload);
};

/**
 * Puts several rows away in one trip, each to its own destination. All or
 * nothing: a 400 carries `error` and `errors` as `[{index, message}]`, the
 * index being the row's position in `items` (1-based).
 *
 * @param {object} payload - `warehouse_id, items: [{source, product_id,
 *   from_slot_id, order_product_id, to_slot_id, quantity, lot, reference}], notes`
 */
export const putawayBulkRequest = async (payload) => {
  return await api.post('inventories/putaway/bulk/', payload);
};

/**
 * Puts away from a sheet: one row per SKU with the quantity and the
 * destination, against what ONE client has in receiving of ONE warehouse.
 * All or nothing: a 400 carries `error` and `errors` as `[{row, message}]`,
 * the row being the line of the Excel.
 *
 * @param {FormData} formData - `excel_file`, `warehouse_id`, `company_id`.
 */
export const putawayUploadRequest = async (formData) => {
  return await api.post('inventories/putaway/upload/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

/**
 * Puts away what was left on the packing table of a cancelled order.
 *
 * @param {object} payload - `order_product_id, to_slot_id, quantity, lot, notes`
 */
export const restockRequest = async (payload) => {
  return await api.post('inventories/putaway/restock/', payload);
};
